import { Component, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ConfirmationService, MessageService, PrimeNGConfig } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MenuService } from '../../mirage/app.menu.service';
import { AppComponent } from '../../../app.component';
import { AppTopBarComponent } from '../../mirage/app-top-bar/app-top-bar.component';
import { AppMenuComponent } from '../../mirage/app-menu/app-menu.component';
import { AuthService, CustomAlertComponent, CustomLoadingPanelComponent, EventBusService, UserActivityService } from 'lib-base';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-authenticated-layout',
  standalone: true,
  imports: [RouterOutlet, CommonModule,
    ToastModule, ConfirmDialogModule, CustomLoadingPanelComponent,
    AppTopBarComponent, AppMenuComponent, CustomAlertComponent],
  providers: [MessageService, ConfirmationService],
  templateUrl: './authenticated-layout.component.html',
  styleUrl: './authenticated-layout.component.scss',
  animations: [
    trigger('mask-anim', [
      state('void', style({
        opacity: 0
      })),
      state('visible', style({
        opacity: 0.8
      })),
      transition('* => *', animate('250ms cubic-bezier(0, 0, 0.2, 1)'))
    ])
  ]
})
export class AuthenticatedLayoutComponent implements OnInit, OnDestroy {
  @Input('active-icon')
  activeIcon !: boolean;

  rightPanelClick !: boolean;
  rightPanelActive !: boolean;
  menuClick !: boolean;
  staticMenuActive !: boolean;
  menuMobileActive !: boolean;
  megaMenuClick !: boolean;
  megaMenuActive !: boolean;
  megaMenuMobileClick !: boolean;
  megaMenuMobileActive !: boolean;
  topbarItemClick !: boolean;
  topbarMobileMenuClick !: boolean;
  topbarMobileMenuActive !: boolean;
  sidebarActive !: boolean;
  activeTopbarItem !: any;
  topbarMenuActive !: boolean;
  menuHoverActive !: boolean;
  configActive !: boolean;

  logoutSubscription !: Subscription;
  private _eventBusSub !: Subscription;
  translations: any = {};

  constructor(public renderer: Renderer2, private menuService: MenuService, private _router: Router, private _cs: ConfirmationService,
    private primengConfig: PrimeNGConfig, public app: AppComponent, private _as: AuthService, private _eb: EventBusService, private _uas: UserActivityService,
    private _t: TranslateService) {
    this.logoutSubscription = this._as.logoutSource$.subscribe(key => {
      this._router.navigate(['/public/login']);
    });
    this._eventBusSub = this._eb.on('logout', () => {
      this._as.logout().subscribe({next: (response) => {}});
    });
    this._eventBusSub = this._eb.on('no-user-activity', () => {
      this._cs.confirm({
        message: this.translations.tiempoLimiteExpirado,
        header: this.translations.tiempoLimite,
        icon: 'pi pi-exclamation-triangle',
        acceptIcon: "none",
        rejectIcon: "none",
        rejectButtonStyleClass: "p-button-text",
        accept: () => {
          this._uas.resetInactivityTimer();
        },
        reject: () => {
          this._as.logout().subscribe({ next: (response) => { 
            this._uas.resetInactivityTimer();
          } });
        }
      });
    });
  }

  async ngOnInit() {
    this.translations = await firstValueFrom(forkJoin({
      tiempoLimiteExpirado: this._t.get('msg.tiempo-limite-expirado'),
      tiempoLimite: this._t.get('msg.tiempo-limite'),
    }));
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
    if (this._eventBusSub) {
      this._eventBusSub.unsubscribe();
    }
  }

  onLayoutClick() {
    if (!this.topbarItemClick) {
      this.activeTopbarItem = null;
      this.topbarMenuActive = false;
    }

    if (!this.rightPanelClick) {
      this.rightPanelActive = false;
    }

    if (!this.megaMenuClick) {
      this.megaMenuActive = false;
    }

    if (!this.megaMenuMobileClick) {
      this.megaMenuMobileActive = false;
    }

    if (!this.menuClick) {
      if (this.isHorizontal()) {
        this.menuService.reset();
      }

      if (this.menuMobileActive) {
        this.menuMobileActive = false;
      }

      this.menuHoverActive = false;
    }

    this.menuClick = false;
    this.topbarItemClick = false;
    this.megaMenuClick = false;
    this.megaMenuMobileClick = false;
    this.rightPanelClick = false;
  }

  onMegaMenuButtonClick(event: any) {
    this.megaMenuClick = true;
    this.megaMenuActive = !this.megaMenuActive;
    event.preventDefault();
  }

  onMegaMenuClick(event: any) {
    this.megaMenuClick = true;
    event.preventDefault();
  }

  onTopbarItemClick(event: any, item: any) {
    this.topbarItemClick = true;

    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    event.preventDefault();
  }

  onRightPanelButtonClick(event: any) {
    this.rightPanelClick = true;
    this.rightPanelActive = !this.rightPanelActive;

    event.preventDefault();
  }

  onRightPanelClose(event: any) {
    this.rightPanelActive = false;
    this.rightPanelClick = false;

    event.preventDefault();
  }

  onRightPanelClick(event: any) {
    this.rightPanelClick = true;

    event.preventDefault();
  }

  onTopbarMobileMenuButtonClick(event: any) {
    this.topbarMobileMenuClick = true;
    this.topbarMobileMenuActive = !this.topbarMobileMenuActive;

    event.preventDefault();
  }

  onMegaMenuMobileButtonClick(event: any) {
    this.megaMenuMobileClick = true;
    this.megaMenuMobileActive = !this.megaMenuMobileActive;

    event.preventDefault();
  }

  onMenuButtonClick(event: any) {
    this.menuClick = true;
    this.topbarMenuActive = false;

    if (this.isMobile()) {
      this.menuMobileActive = !this.menuMobileActive;
    }

    event.preventDefault();
  }

  onSidebarClick(event: Event) {
    this.menuClick = true;
  }

  onToggleMenuClick(event: Event) {
    this.staticMenuActive = !this.staticMenuActive;
    event.preventDefault();
  }

  onRippleChange(event: any) {
    this.app.ripple = event.checked;
    this.primengConfig = event.checked;
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return window.innerWidth <= 991;
  }

  isHorizontal() {
    return this.app.horizontalMenu === true;
  }
}
